import { useState, useEffect } from 'react';
import { FaTimes, FaExclamationTriangle, FaEdit } from 'react-icons/fa';
import { validarCpfCnpj, formatarCpfCnpj, formatarTelefone } from '../utils/validators';
import { clientesAPI } from '../services/api';

const ClientForm = ({ onClose, onSuccess, clienteEdit = null }) => {
  const [formData, setFormData] = useState({
    nome: '',
    cpf_cnpj: '',
    endereco: '',
    telefone: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [cpfDuplicado, setCpfDuplicado] = useState(null);

  useEffect(() => {
    if (clienteEdit) {
      setFormData({
        nome: clienteEdit.nome,
        cpf_cnpj: clienteEdit.cpf_cnpj || '',
        endereco: clienteEdit.endereco || '',
        telefone: clienteEdit.telefone || '',
      });
      setCpfDuplicado(null);
    }
  }, [clienteEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));

    if (name === 'cpf_cnpj') {
      setCpfDuplicado(null);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    const cpfCnpjDigits = formData.cpf_cnpj.replace(/\D/g, '');
    if (cpfCnpjDigits.length > 0 && !validarCpfCnpj(formData.cpf_cnpj)) {
      newErrors.cpf_cnpj = 'CPF/CNPJ inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (clienteEdit) {
        await clientesAPI.atualizar(clienteEdit.id, formData);
        onSuccess('Cliente atualizado com sucesso!');
      } else {
        // Verificar CPF/CNPJ duplicado antes de criar
        const cpfCnpjLimpo = formData.cpf_cnpj.replace(/\D/g, '');
        if (cpfCnpjLimpo) {
          const { data } = await clientesAPI.verificarCpfCnpj(cpfCnpjLimpo);

          if (data.existe) {
            setCpfDuplicado(data.cliente);
            setLoading(false);
            return;
          }
        }

        await clientesAPI.criar(formData);
        onSuccess('Cliente cadastrado com sucesso!');
      }
    } catch (error) {
      if (error.response?.data?.error) {
        setErrors({ submit: error.response.data.error });
      } else {
        setErrors({ submit: 'Erro ao salvar cliente' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditarClienteExistente = () => {
    if (cpfDuplicado) {
      onSuccess(null, cpfDuplicado);
    }
  };

  // Se detectou CPF/CNPJ duplicado, mostrar alerta especial
  if (cpfDuplicado) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
          <div className="bg-yellow-500 text-white p-6 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <FaExclamationTriangle size={24} />
              <h2 className="text-xl font-bold">CPF/CNPJ já Cadastrado</h2>
            </div>
            <button
              onClick={() => setCpfDuplicado(null)}
              className="hover:bg-yellow-600 p-2 rounded-full transition duration-200"
            >
              <FaTimes size={20} />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <p className="text-gray-700">
              Este CPF/CNPJ já está cadastrado no sistema para o cliente:
            </p>

            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <p className="font-semibold text-gray-800">{cpfDuplicado.nome}</p>
              {cpfDuplicado.cpf_cnpj && (
                <p className="text-sm text-gray-600">CPF/CNPJ: {formatarCpfCnpj(cpfDuplicado.cpf_cnpj)}</p>
              )}
              {cpfDuplicado.telefone && (
                <p className="text-sm text-gray-600">
                  Telefone: {formatarTelefone(cpfDuplicado.telefone)}
                </p>
              )}
            </div>

            <p className="text-gray-700">
              Deseja editar os dados deste cliente?
            </p>
          </div>

          <div className="bg-gray-50 p-6 rounded-b-lg flex justify-end space-x-3">
            <button
              onClick={() => setCpfDuplicado(null)}
              className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold rounded-lg transition duration-200"
            >
              Cancelar
            </button>
            <button
              onClick={handleEditarClienteExistente}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition duration-200 flex items-center space-x-2"
            >
              <FaEdit />
              <span>Editar Cliente</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="bg-blue-600 text-white p-6 rounded-t-lg flex items-center justify-between">
          <h2 className="text-2xl font-bold">
            {clienteEdit ? 'Editar Cliente' : 'Novo Cliente'}
          </h2>
          <button
            onClick={onClose}
            className="hover:bg-blue-700 p-2 rounded-full transition duration-200"
          >
            <FaTimes size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Nome */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome Completo *
            </label>
            <input
              type="text"
              name="nome"
              value={formData.nome}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.nome ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Digite o nome completo"
            />
            {errors.nome && (
              <p className="mt-1 text-sm text-red-600">{errors.nome}</p>
            )}
          </div>

          {/* CPF/CNPJ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CPF/CNPJ
            </label>
            <input
              type="text"
              name="cpf_cnpj"
              value={formData.cpf_cnpj}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.cpf_cnpj ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="000.000.000-00 ou 00.000.000/0000-00"
              maxLength="18"
            />
            {errors.cpf_cnpj && (
              <p className="mt-1 text-sm text-red-600">{errors.cpf_cnpj}</p>
            )}
          </div>

          {/* Endereço */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Endereço
            </label>
            <input
              type="text"
              name="endereco"
              value={formData.endereco}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Digite o endereço completo"
            />
          </div>

          {/* Telefone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Telefone
            </label>
            <input
              type="text"
              name="telefone"
              value={formData.telefone}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="(00) 00000-0000"
              maxLength="15"
            />
          </div>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {errors.submit}
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold rounded-lg transition duration-200"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Salvando...' : clienteEdit ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientForm;
